import React, { createRef } from 'react';
import {
  canUseBlacklist,
  canUseMatchRules,
} from '../../constants/planFeatures';

// styling
import './Customer.css';
import './CustomerProfile.css';

// constants + icons
import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import CloseIcon from '@material-ui/icons/Close';
import SmsIcon from '@material-ui/icons/Sms';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import {
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { Typography } from '@mui/material';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {
  ROLE_ADMIN,
  ROLE_OWNER,
  ROLE_MASTER_ADMIN,
  ROLE_MANAGER,
  ROLE_TECH_ADMIN,
} from '../global/constants';
import {
  AUDIT,
  BASE_URL,
  CUSTOMER,
  VERIFICATION,
  RULES,
} from '../global/Server/endpoints';
import WriteGuard from '../global/ReadOnly/WriteGuard';
import {
  attachStream,
  captureFrame,
  dataURLToPhotoFile,
  describeCameraError,
  listVideoInputs,
  openCameraStream,
  stopStream,
} from './cameraUtils';

const POINTS_EDIT_ROLES = [
  ROLE_ADMIN,
  ROLE_OWNER,
  ROLE_MASTER_ADMIN,
  ROLE_MANAGER,
  ROLE_TECH_ADMIN,
];

// Who may see the permanent point-change trail: floor manager, company owner,
// NDS owner and tech admin. Mirrors AuditController.listCustomerPointAudits.
const POINT_AUDIT_ROLES = [
  ROLE_OWNER,
  ROLE_MASTER_ADMIN,
  ROLE_TECH_ADMIN,
  ROLE_MANAGER,
];

// Rows fetched per "Show more" click in the point-history list.
const POINT_AUDIT_PAGE_SIZE = 10;

const ROLE_WALLET = 'ROLE_CUSTOMER_WALLET_PASS';

const TAB_INFO = 'info';
const TAB_POINTS = 'points';
const TAB_RULES = 'rules';
const TAB_WALLET = 'wallet';

const TABS = [
  { id: TAB_INFO, label: 'Info' },
  { id: TAB_POINTS, label: 'Points' },
  { id: TAB_RULES, label: 'Match Rules' },
  { id: TAB_WALLET, label: 'Wallet' },
];

const STATUS_ACTIVE = 'Active';
const STATUS_UNACTIVE = 'Unactive';
const STATUS_BANNED = 'Banned';

// Format a date/string safely for date inputs
const toDateOrNull = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

// Format a stored dob for read-only display
const formatDisplayDate = (v) => {
  if (!v) return '—';
  const d = toDateOrNull(v);
  if (!d) return String(v);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const formatPhone = (v) => {
  if (!v) return '—';
  const digits = String(v).replace(/\D/g, '');
  if (digits.length !== 10) return v;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const initialsFor = (customer) => {
  const f = (customer?.firstName || '').trim().charAt(0).toUpperCase();
  const l = (customer?.lastName || '').trim().charAt(0).toUpperCase();
  return `${f}${l}` || '?';
};

// Map persisted flags (isBlocked, isInactive) to the three-state pill.
// Banned takes precedence over Unactive if both flags happen to be true.
const statusFromCustomer = (customer) => {
  if (!customer) return STATUS_ACTIVE;
  if (customer.isBlocked === true) return STATUS_BANNED;
  if (customer.isInactive === true) return STATUS_UNACTIVE;
  return STATUS_ACTIVE;
};

class CustomerProfile extends React.Component {
  constructor(props) {
    super(props);
    const c = props.customer || {};
    this.state = {
      activeTab: TAB_INFO,

      // Info editing
      infoEditing: false,
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: c.phone || '',
      dob: toDateOrNull(c.dob),
      comments: c.comments || '',

      // Points
      pointsEditing: false,
      points:
        typeof c.points === 'number' ? c.points : parseInt(c.points, 10) || 0,
      statusDraft: statusFromCustomer(c),

      // Match rules
      isRule: !!c.isRule,
      timeLimit: c.timeLimit != null ? c.timeLimit : '',
      maxMatchesPerDay: c.maxMatchesPerDay != null ? c.maxMatchesPerDay : '',
      maxAmountPerDay: c.maxAmountPerDay != null ? c.maxAmountPerDay : '',
      matchValues: Array.isArray(c.matchValues) ? [...c.matchValues] : [],
      rulesDirty: false,

      // Wallet
      walletType: 'apple',
      walletQrUrl: null,
      walletPassUrl: null,
      walletDialogOpen: false,
      isGeneratingWallet: false,
      walletChecked: false,

      // Photo retake (Info tab edit mode)
      showVideo: false,
      cameraFacingMode: 'environment',
      capturedImageURL: null,
      newPhotoFile: null,

      // Camera health — see cameraUtils.js. Without these a blank "camera
      // off" frame was indistinguishable from a real photo and overwrote
      // the customer's existing picture.
      cameraError: '',
      cameraStarting: false,
      cameraReady: false,
      videoInputs: [],
      selectedCameraId: '',
      rejectedFrameURL: null,
      rejectedFrameReason: '',

      // Photo zoom dialog
      photoZoomOpen: false,

      // Status menu (header dropdown)
      statusMenuAnchor: null,

      // Recent visits (last 20) — lazy-loaded on first Points tab view
      recentVisits: [],
      recentVisitsLoaded: false,
      recentVisitsLoading: false,

      // Point-change audit — lazy-loaded on first Points tab view, paged 10
      // at a time (newest first). Restricted to POINT_AUDIT_ROLES.
      pointAudits: [],
      pointAuditTotal: 0,
      pointAuditHasMore: false,
      pointAuditLoaded: false,
      pointAuditLoading: false,
      pointAuditLoadingMore: false,

      // ── Phone re-verification on edit (NDS Phone Verification) ──────────
      // Only enforced when the location requires verification AND the phone
      // in the edit form differs from the customer's stored number.
      verificationRequired: true, // refined from the location rule on mount
      verifyStep: 'idle', // 'idle' → 'linkSent' → 'verified'
      verifiedPhone: '', // the exact 10-digit value that was verified
      sendingLink: false,
      codeDigits: ['', '', '', '', '', ''],
      verifying: false,
      verifyError: '',
      resendIn: 0,
    };
    this.videoRef = createRef();
    this.canvasRef = createRef();
    this.codeRefs = Array.from({ length: 6 }, () => createRef());
  }

  componentWillUnmount() {
    this.stopCamera();
    if (this.resendTimer) clearInterval(this.resendTimer);
  }

  componentDidMount() {
    if (this.hasWalletRole() && this.props.customer?.customerId) {
      this.fetchExistingWalletPass();
    }
    // Load the location's "Require Phone Verification" rule so an edit that
    // changes the phone forces re-verification only where it's turned on.
    const locationId =
      this.props.customer?.locationId || this.props.user?.selectedLocationId;
    if (locationId) {
      this.authFetch(RULES.LIST_RULES, { locationId })
        .then((data) => {
          const rule = Array.isArray(data?.rules) ? data.rules[0] : null;
          if (rule && rule.requirePhoneVerification === false) {
            this.setState({ verificationRequired: false });
          }
        })
        .catch(() => {
          /* keep the safe default (required) on any error */
        });
    }
  }

  // ── Phone re-verification helpers ──────────────────────────────────────────
  authFetch = (endpoint, body) => {
    const token = localStorage.getItem('token');
    return fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) => res.json());
  };

  editLocationId = () =>
    this.props.customer?.locationId ||
    this.props.user?.selectedLocationId ||
    '';

  // True when the edited phone is a valid 10-digit number that differs from
  // the customer's currently-stored number.
  phoneChanged = () => {
    const cur = (this.state.phone || '').replace(/\D/g, '');
    const orig = (this.props.customer?.phone || '').replace(/\D/g, '');
    return cur.length === 10 && cur !== orig;
  };

  // True when the changed phone must be re-verified before we can save.
  needsPhoneReverify = () =>
    this.state.verificationRequired && this.phoneChanged();

  // True once the current phone value has been verified this edit session.
  phoneReverified = () => {
    const cur = (this.state.phone || '').replace(/\D/g, '');
    return (
      this.state.verifyStep === 'verified' &&
      (this.state.verifiedPhone || '').replace(/\D/g, '') === cur
    );
  };

  resetVerification = () =>
    this.setState({
      verifyStep: 'idle',
      verifiedPhone: '',
      codeDigits: ['', '', '', '', '', ''],
      verifyError: '',
      resendIn: 0,
    });

  focusBox = (i) => {
    const ref = this.codeRefs[i];
    if (ref && ref.current) ref.current.focus();
  };

  handleCodeChange = (index, rawValue) => {
    const value = (rawValue || '').replace(/\D/g, '');
    const digits = [...this.state.codeDigits];
    if (value.length > 1) {
      const chars = value.slice(0, 6 - index).split('');
      chars.forEach((c, i) => {
        digits[index + i] = c;
      });
      this.setState({ codeDigits: digits, verifyError: '' });
      this.focusBox(Math.min(index + chars.length, 5));
      return;
    }
    digits[index] = value;
    this.setState({ codeDigits: digits, verifyError: '' });
    if (value && index < 5) this.focusBox(index + 1);
  };

  handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !this.state.codeDigits[index] && index > 0) {
      this.focusBox(index - 1);
    }
  };

  sendVerificationLink = async () => {
    const phone = (this.state.phone || '').replace(/\D/g, '');
    if (phone.length !== 10 || this.state.sendingLink) return;
    this.setState({ sendingLink: true, verifyError: '' });
    try {
      const data = await this.authFetch(VERIFICATION.SEND_LINK, {
        phone,
        locationId: this.editLocationId(),
      });
      if (data.success) {
        this.setState({
          verifyStep: 'linkSent',
          codeDigits: ['', '', '', '', '', ''],
        });
        this.startResendCountdown();
        this.props.openFeedbackBar(
          'success',
          'Link sent. Ask the customer to accept consent on their phone, then read you the code.'
        );
      } else {
        this.props.openFeedbackBar(
          'error',
          data.message || 'Unable to send the link.'
        );
      }
    } catch (e) {
      this.props.openFeedbackBar('error', 'Network error. Please try again.');
    } finally {
      this.setState({ sendingLink: false });
    }
  };

  verifyCode = async () => {
    const code = this.state.codeDigits.join('');
    const phone = (this.state.phone || '').replace(/\D/g, '');
    if (code.length !== 6 || this.state.verifying) return;
    this.setState({ verifying: true, verifyError: '' });
    try {
      const data = await this.authFetch(VERIFICATION.VERIFY_CODE, {
        phone,
        code,
        locationId: this.editLocationId(),
      });
      if (data.verified) {
        if (this.resendTimer) clearInterval(this.resendTimer);
        this.setState({ verifyStep: 'verified', verifiedPhone: phone });
        this.props.openFeedbackBar('success', 'Phone verified successfully!');
      } else {
        this.setState({ verifyError: data.error || 'Incorrect code.' });
      }
    } catch (e) {
      this.setState({ verifyError: 'Network error. Please try again.' });
    } finally {
      this.setState({ verifying: false });
    }
  };

  resendVerification = async () => {
    if (this.state.resendIn > 0) return;
    const phone = (this.state.phone || '').replace(/\D/g, '');
    try {
      const data = await this.authFetch(VERIFICATION.RESEND_CODE, {
        phone,
        locationId: this.editLocationId(),
      });
      if (data.success) {
        this.setState({
          codeDigits: ['', '', '', '', '', ''],
          verifyError: '',
        });
        this.startResendCountdown();
      } else {
        this.props.openFeedbackBar(
          'error',
          data.message || 'Please wait before requesting a new code.'
        );
        if (data.cooldownRemaining)
          this.startResendCountdown(data.cooldownRemaining);
      }
    } catch (e) {
      this.props.openFeedbackBar('error', 'Network error. Please try again.');
    }
  };

  startResendCountdown = (seconds = 60) => {
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.setState({ resendIn: seconds });
    this.resendTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendIn <= 1) {
          clearInterval(this.resendTimer);
          return { resendIn: 0 };
        }
        return { resendIn: prev.resendIn - 1 };
      });
    }, 1000);
  };

  componentDidUpdate(prevProps) {
    // If the profile customer reference changes (e.g., save returned a
    // fresher copy from Redux), rehydrate readable fields.
    if (prevProps.customer !== this.props.customer && this.props.customer) {
      const c = this.props.customer;
      this.setState((prev) => ({
        firstName: prev.infoEditing ? prev.firstName : c.firstName || '',
        lastName: prev.infoEditing ? prev.lastName : c.lastName || '',
        phone: prev.infoEditing ? prev.phone : c.phone || '',
        dob: prev.infoEditing ? prev.dob : toDateOrNull(c.dob),
        comments: prev.infoEditing ? prev.comments : c.comments || '',
        points: prev.pointsEditing
          ? prev.points
          : typeof c.points === 'number'
          ? c.points
          : parseInt(c.points, 10) || 0,
        statusDraft: prev.pointsEditing
          ? prev.statusDraft
          : statusFromCustomer(c),
        isRule: prev.rulesDirty ? prev.isRule : !!c.isRule,
        timeLimit: prev.rulesDirty
          ? prev.timeLimit
          : c.timeLimit != null
          ? c.timeLimit
          : '',
        maxMatchesPerDay: prev.rulesDirty
          ? prev.maxMatchesPerDay
          : c.maxMatchesPerDay != null
          ? c.maxMatchesPerDay
          : '',
        maxAmountPerDay: prev.rulesDirty
          ? prev.maxAmountPerDay
          : c.maxAmountPerDay != null
          ? c.maxAmountPerDay
          : '',
        matchValues: prev.rulesDirty
          ? prev.matchValues
          : Array.isArray(c.matchValues)
          ? [...c.matchValues]
          : [],
      }));
    }
  }

  hasWalletRole = () => {
    const roles = this.props.user?.authority || [];
    return roles.includes(ROLE_WALLET);
  };

  isAdmin = () => {
    const roles = this.props.user?.authority || [];
    return roles.includes(ROLE_ADMIN);
  };

  canEditPoints = () => {
    const roles = this.props.user?.authority || [];
    return POINTS_EDIT_ROLES.some((r) => roles.includes(r));
  };

  canViewPointAudit = () => {
    const roles = this.props.user?.authority || [];
    return POINT_AUDIT_ROLES.some((r) => roles.includes(r));
  };

  // ── Camera (photo retake in Info edit) ──
  openCamera = () => {
    this.setState(
      {
        showVideo: true,
        capturedImageURL: null,
        newPhotoFile: null,
        cameraError: '',
        rejectedFrameURL: null,
        rejectedFrameReason: '',
      },
      () => this.startWebcam()
    );
  };

  startWebcam = async (deviceId) => {
    if (!this.videoRef.current) return;
    stopStream(this.videoRef.current);
    this.setState({
      cameraStarting: true,
      cameraError: '',
      cameraReady: false,
    });
    try {
      const stream = await openCameraStream({
        deviceId: deviceId || this.state.selectedCameraId,
        facingMode: this.state.cameraFacingMode,
      });
      if (!this.videoRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      await attachStream(this.videoRef.current, stream);
      const videoInputs = await listVideoInputs();
      const active = stream.getVideoTracks()[0];
      const activeId = active?.getSettings ? active.getSettings().deviceId : '';
      this.setState({
        cameraStarting: false,
        cameraReady: true,
        cameraError: '',
        videoInputs,
        selectedCameraId: deviceId || this.state.selectedCameraId || activeId,
      });
    } catch (err) {
      stopStream(this.videoRef.current);
      const message = describeCameraError(err);
      this.props.openFeedbackBar('error', message);
      this.setState({
        showVideo: false,
        cameraStarting: false,
        cameraReady: false,
        cameraError: message,
      });
    }
  };

  handleSelectCamera = (e) => {
    const deviceId = e.target.value;
    this.setState({ selectedCameraId: deviceId }, () => {
      if (this.state.showVideo) this.startWebcam(deviceId);
    });
  };

  switchCamera = () => {
    const nextFacingMode =
      this.state.cameraFacingMode === 'environment' ? 'user' : 'environment';
    // Drop any pinned deviceId — it would take priority over facingMode.
    this.setState(
      { cameraFacingMode: nextFacingMode, selectedCameraId: '' },
      () => {
        if (this.state.showVideo) {
          this.startWebcam();
        }
      }
    );
  };

  stopCamera = () => {
    stopStream(this.videoRef.current);
    this.setState({ showVideo: false, cameraReady: false });
  };

  captureImage = () => {
    const result = captureFrame(this.videoRef.current, this.canvasRef.current, {
      quality: 0.5,
    });
    if (!result.dataURL) {
      this.props.openFeedbackBar('error', result.reason);
      this.setState({ cameraError: result.reason });
      return;
    }
    if (!result.ok) {
      // Held out of `newPhotoFile` so it cannot be submitted and overwrite
      // the customer's existing photo with a blank frame.
      this.setState({
        rejectedFrameURL: result.dataURL,
        rejectedFrameReason: result.reason,
        capturedImageURL: null,
        newPhotoFile: null,
        cameraError: '',
      });
      this.stopCamera();
      return;
    }
    this.setState(
      {
        capturedImageURL: result.dataURL,
        rejectedFrameURL: null,
        rejectedFrameReason: '',
        cameraError: '',
      },
      () => this.stopCamera()
    );
    this.addCapturedPhotoToState(result.dataURL);
  };

  handleUseRejectedFrame = () => {
    const dataURL = this.state.rejectedFrameURL;
    if (!dataURL) return;
    this.setState({
      capturedImageURL: dataURL,
      rejectedFrameURL: null,
      rejectedFrameReason: '',
    });
    this.addCapturedPhotoToState(dataURL);
  };

  handleRetakePhoto = () => {
    this.setState({ rejectedFrameURL: null, rejectedFrameReason: '' }, () =>
      this.openCamera()
    );
  };

  addCapturedPhotoToState = async (fileDataURL) => {
    try {
      const newPhotoFile = await dataURLToPhotoFile(fileDataURL);
      this.setState({
        newPhotoFile,
        capturedImageURL: newPhotoFile.localURL,
      });
    } catch (err) {
      const message =
        'The captured photo could not be prepared for upload. Please take it again.';
      this.props.openFeedbackBar('error', message);
      this.setState({
        capturedImageURL: null,
        newPhotoFile: null,
        cameraError: message,
      });
    }
  };

  // ── Shared save helper ──
  submitCustomerUpdate = (overrides, successMsg, onSuccess) => {
    const c = this.props.customer || {};
    const payload = {
      locationId: c.locationId || this.props.user?.selectedLocationId || '',
      customerId: c.customerId,
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      dob: this.state.dob,
      points: parseInt(this.state.points, 10) || 0,
      driverLicense: c.driverLicense || '',
      phone: this.state.phone,
      address: c.address || '',
      comments: this.state.comments,
      files: this.state.newPhotoFile
        ? [this.state.newPhotoFile]
        : c.files || [],
      newFiles: this.state.newPhotoFile ? [this.state.newPhotoFile] : [],
      isRule: this.state.isRule,
      maxMatchesPerDay:
        this.state.maxMatchesPerDay === '' ? null : this.state.maxMatchesPerDay,
      timeLimit: this.state.timeLimit === '' ? null : this.state.timeLimit,
      maxAmountPerDay:
        this.state.maxAmountPerDay === '' ? null : this.state.maxAmountPerDay,
      isBlocked: this.state.statusDraft === STATUS_BANNED,
      isInactive: this.state.statusDraft === STATUS_UNACTIVE,
      matchValues: (this.state.matchValues || []).filter((v) => v !== ''),
      userId: this.props.user?.userId,
      employeeName: [this.props.user?.firstname, this.props.user?.lastname]
        .filter((p) => p && p !== 'null' && p !== 'undefined')
        .join(' '),
      capturedImageURL:
        this.state.capturedImageURL || c.capturedImageURL || null,
      ...overrides,
    };

    this.props.saveCustomer(
      payload,
      () => {
        this.props.openFeedbackBar('success', successMsg || 'Saved');
        if (onSuccess) onSuccess();
      },
      (message) => {
        this.props.openFeedbackBar('error', message);
      }
    );
  };

  // ── Tabs ─────────────────────────────────────────────────────────────────
  setTab = (tabId) => {
    this.setState({ activeTab: tabId });
    if (tabId === TAB_POINTS && !this.state.recentVisitsLoaded) {
      this.fetchRecentVisits();
    }
    if (
      tabId === TAB_POINTS &&
      !this.state.pointAuditLoaded &&
      this.canViewPointAudit()
    ) {
      this.fetchPointAudits(0);
    }
  };

  fetchRecentVisits = async () => {
    const customerId = this.props.customer?.customerId;
    if (!customerId) return;

    this.setState({ recentVisitsLoading: true });
    try {
      const response = await fetch(CUSTOMER.GET_RECENT_VISITS, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.success) {
          this.setState({
            recentVisits: Array.isArray(data.recentVisits)
              ? data.recentVisits
              : [],
          });
        }
      }
    } catch (err) {
      // silent — visits panel will show its empty state
    } finally {
      this.setState({
        recentVisitsLoading: false,
        recentVisitsLoaded: true,
      });
    }
  };

  // Newest-first page of the permanent point-change trail. offset 0 replaces
  // the list (initial load / refresh after a save); any other offset appends
  // the next page behind the "Show more" button.
  fetchPointAudits = async (offset = 0) => {
    const customerId = this.props.customer?.customerId;
    if (!customerId || !this.canViewPointAudit()) return;

    const isFirstPage = offset === 0;
    this.setState(
      isFirstPage
        ? { pointAuditLoading: true }
        : { pointAuditLoadingMore: true }
    );
    try {
      const data = await this.authFetch(AUDIT.LIST_CUSTOMER_POINT_AUDIT, {
        customerId,
        offset,
        max: POINT_AUDIT_PAGE_SIZE,
      });
      if (data && data.success) {
        const page = Array.isArray(data.logs) ? data.logs : [];
        this.setState((prev) => ({
          pointAudits: isFirstPage ? page : [...prev.pointAudits, ...page],
          pointAuditTotal: data.total || 0,
          pointAuditHasMore: !!data.hasMore,
        }));
      }
    } catch (err) {
      // silent — the panel falls back to its empty state
    } finally {
      this.setState({
        pointAuditLoading: false,
        pointAuditLoadingMore: false,
        pointAuditLoaded: true,
      });
    }
  };

  // ── Info tab ─────────────────────────────────────────────────────────────
  beginInfoEdit = () => this.setState({ infoEditing: true });

  cancelInfoEdit = () => {
    const c = this.props.customer || {};
    this.stopCamera();
    this.setState({
      infoEditing: false,
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: c.phone || '',
      dob: toDateOrNull(c.dob),
      comments: c.comments || '',
      capturedImageURL: null,
      newPhotoFile: null,
      showVideo: false,
    });
    this.resetVerification();
  };

  saveInfo = () => {
    // Block the save until the new number is verified at locations that
    // require it — mirrors the server-side gate so the UX matches.
    if (this.needsPhoneReverify() && !this.phoneReverified()) {
      this.props.openFeedbackBar(
        'error',
        'Please verify the new phone number before saving.'
      );
      return;
    }
    this.submitCustomerUpdate({}, 'Info updated');
    this.stopCamera();
    this.setState({
      infoEditing: false,
      capturedImageURL: null,
      newPhotoFile: null,
      showVideo: false,
    });
    this.resetVerification();
  };

  handlePhoneInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    this.setState({ phone: digits });
    // Editing the number invalidates any in-progress or completed
    // verification — the new value hasn't been proven yet.
    if (this.state.verifyStep !== 'idle') {
      this.resetVerification();
    }
  };

  // Inline verification shown under the Phone field in edit mode when the
  // number changed and the location requires verification. Hidden otherwise.
  renderPhoneReverify = () => {
    if (!this.needsPhoneReverify() || this.phoneReverified()) return null;
    const {
      verifyStep,
      sendingLink,
      codeDigits,
      verifying,
      verifyError,
      resendIn,
    } = this.state;

    if (verifyStep === 'idle') {
      return (
        <Button
          className="cust-verify-send"
          startIcon={<SmsIcon />}
          onClick={this.sendVerificationLink}
          disabled={sendingLink}
          fullWidth
        >
          {sendingLink ? 'Sending…' : 'Send Verification Code to Customer'}
        </Button>
      );
    }

    return (
      <div className="cust-verify-box">
        <div className="cust-verify-head">
          <div className="cust-verify-icon">
            <SmsIcon />
          </div>
          <div>
            <div className="cust-verify-title">Code Sent to Customer!</div>
            <div className="cust-verify-sub">
              Ask the customer for the 6-digit code
            </div>
          </div>
        </div>

        <div className="cust-code-row">
          {codeDigits.map((d, i) => (
            <input
              key={i}
              ref={this.codeRefs[i]}
              className={`cust-code-input${d ? ' filled' : ''}`}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => this.handleCodeChange(i, e.target.value)}
              onKeyDown={(e) => this.handleCodeKeyDown(i, e)}
            />
          ))}
        </div>

        {verifyError ? (
          <div className="cust-code-err">{verifyError}</div>
        ) : null}

        <Button
          className="cust-verify-confirm"
          onClick={this.verifyCode}
          disabled={verifying || codeDigits.join('').length !== 6}
          fullWidth
        >
          {verifying ? 'Verifying…' : 'Verify Code'}
        </Button>

        <button
          type="button"
          className={`cust-resend${resendIn > 0 ? ' disabled' : ''}`}
          onClick={this.resendVerification}
          disabled={resendIn > 0}
        >
          {resendIn > 0 ? `Wait ${resendIn}s to resend` : 'Resend Code'}
        </button>

        <div className="cust-verify-note">
          The customer received an SMS with a link to review &amp; accept the
          consent. Once they accept, their check-in code appears on their phone
          — ask them to read it to you and type it above.
        </div>
      </div>
    );
  };

  // ── Status dropdown (header) ─────────────────────────────────────────────
  openStatusMenu = (e) => {
    this.setState({ statusMenuAnchor: e.currentTarget });
  };

  closeStatusMenu = () => {
    this.setState({ statusMenuAnchor: null });
  };

  handleStatusChange = (newStatus) => {
    this.setState({ statusDraft: newStatus, statusMenuAnchor: null });
    if (newStatus === statusFromCustomer(this.props.customer)) return;
    this.submitCustomerUpdate(
      {
        isBlocked: newStatus === STATUS_BANNED,
        isInactive: newStatus === STATUS_UNACTIVE,
      },
      'Status updated'
    );
  };

  // ── Points tab ───────────────────────────────────────────────────────────
  beginPointsEdit = () => {
    if (!this.canEditPoints()) return;
    this.setState({ pointsEditing: true });
  };

  cancelPointsEdit = () => {
    const c = this.props.customer || {};
    this.setState({
      pointsEditing: false,
      points:
        typeof c.points === 'number' ? c.points : parseInt(c.points, 10) || 0,
      statusDraft: statusFromCustomer(c),
    });
  };

  savePoints = () => {
    // Reload page 1 of the trail once the save lands so the new entry is
    // already at the top when the editor closes.
    this.submitCustomerUpdate({}, 'Points updated', () =>
      this.fetchPointAudits(0)
    );
    this.setState({ pointsEditing: false });
  };

  adjustPoints = (delta) => {
    const next = Math.max(0, (parseInt(this.state.points, 10) || 0) + delta);
    this.setState({ points: next });
  };

  // ── Rules tab ────────────────────────────────────────────────────────────
  toggleRule = () => {
    if (!this.isAdmin()) return;
    this.setState((prev) => ({ isRule: !prev.isRule, rulesDirty: true }));
  };

  handleRuleField = (e) => {
    const { name, value } = e.target;
    const next = { [name]: value, rulesDirty: true };

    // Auto-split the Daily Match Cap into two equal match amounts so an
    // admin entering "100" gets [50, 50] without having to type the pair
    // by hand. They can still edit either amount or + Add Match Amount
    // afterwards.
    if (name === 'maxAmountPerDay') {
      const cap = Number(value);
      if (!Number.isNaN(cap) && cap > 0) {
        const half = cap / 2;
        next.matchValues = [String(half), String(half)];
      } else if (value === '' || value === null) {
        next.matchValues = [];
      }
    }

    this.setState(next);
  };

  handleMatchValueChange = (idx, value) => {
    const updated = [...this.state.matchValues];
    updated[idx] = value;
    this.setState({ matchValues: updated, rulesDirty: true });
  };

  addMatchValue = () => {
    this.setState((prev) => ({
      matchValues: [...prev.matchValues, ''],
      rulesDirty: true,
    }));
  };

  removeMatchValue = (idx) => {
    this.setState((prev) => ({
      matchValues: prev.matchValues.filter((_, i) => i !== idx),
      rulesDirty: true,
    }));
  };

  saveRules = () => {
    this.submitCustomerUpdate({}, 'Match rules updated');
    this.setState({ rulesDirty: false });
  };

  // ── Wallet tab ───────────────────────────────────────────────────────────
  fetchExistingWalletPass = async () => {
    const customerId = this.props.customer?.customerId;
    if (!customerId) return;

    try {
      this.setState({ isGeneratingWallet: true });
      const response = await fetch(
        `${BASE_URL}/wallet/existing/${customerId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.qrUrl) {
          this.setState({
            walletQrUrl: data.qrUrl,
            walletPassUrl: data.passUrl,
            walletType: data.walletType || 'apple',
          });
        }
      }
    } catch (err) {
      // silent
    } finally {
      this.setState({ isGeneratingWallet: false, walletChecked: true });
    }
  };

  generateWalletPass = async () => {
    const customerId = this.props.customer?.customerId;
    if (!customerId) return;

    try {
      this.setState({ isGeneratingWallet: true });

      const payload = {
        customerId,
        phone: this.state.phone || this.props.customer?.phone,
        walletType: this.state.walletType,
        locationId: this.props.user?.selectedLocationId,
      };

      const response = await fetch(`${BASE_URL}/wallet/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        this.props.openFeedbackBar('error', 'Failed to generate wallet pass');
        return;
      }

      const data = await response.json();
      this.setState({
        walletQrUrl: data.qrUrl,
        walletPassUrl: data.passUrl,
        walletDialogOpen: true,
      });
    } catch (err) {
      this.props.openFeedbackBar('error', err.message);
    } finally {
      this.setState({ isGeneratingWallet: false });
    }
  };

  openWalletDialog = () => this.setState({ walletDialogOpen: true });
  closeWalletDialog = () => this.setState({ walletDialogOpen: false });

  openPhotoZoom = () => this.setState({ photoZoomOpen: true });
  closePhotoZoom = () => this.setState({ photoZoomOpen: false });

  // ── Rendering ────────────────────────────────────────────────────────────
  renderHeader = () => {
    const c = this.props.customer || {};
    const status = statusFromCustomer(c);

    let statusClass = 'customer-status-active';
    if (status === STATUS_BANNED) statusClass = 'customer-status-banned';
    else if (status === STATUS_UNACTIVE)
      statusClass = 'customer-status-unactive';

    const fullName =
      `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Customer';

    const avatarUrl =
      this.state.capturedImageURL ||
      c.capturedImageURL ||
      (c.files && c.files[0] && c.files[0].localURL) ||
      null;

    return (
      <div className="customer-profile-header">
        <div
          className="customer-avatar"
          onClick={avatarUrl ? this.openPhotoZoom : undefined}
          style={avatarUrl ? { cursor: 'zoom-in' } : undefined}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={fullName}
              className="customer-avatar-img"
            />
          ) : (
            initialsFor(c)
          )}
        </div>
        <div className="customer-header-name-block">
          <div className="customer-header-name">{fullName}</div>
          <div className="customer-header-phone">
            {formatPhone(c.phone)}
            {c.phoneVerified && (
              <CheckCircleIcon
                titleAccess="Phone verified"
                className="customer-verified-icon"
              />
            )}
          </div>
        </div>
        <button
          type="button"
          className={`customer-status-pill customer-status-pill-button ${statusClass} ${
            Boolean(this.state.statusMenuAnchor)
              ? 'customer-status-pill-button-open'
              : ''
          }`}
          onClick={this.openStatusMenu}
          aria-label="Change customer status"
        >
          <span className="customer-status-dot" />
          {status}
          <ArrowDropDownIcon
            className={`customer-status-pill-chevron ${
              Boolean(this.state.statusMenuAnchor)
                ? 'customer-status-pill-chevron-open'
                : ''
            }`}
          />
        </button>
        <Menu
          anchorEl={this.state.statusMenuAnchor}
          open={Boolean(this.state.statusMenuAnchor)}
          onClose={this.closeStatusMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PopoverClasses={{ paper: 'customer-status-menu-paper' }}
          MenuListProps={{ className: 'customer-status-menu-list' }}
          transitionDuration={180}
        >
          {[
            { value: STATUS_ACTIVE, dotClass: 'customer-status-dot-active' },
            {
              value: STATUS_UNACTIVE,
              dotClass: 'customer-status-dot-unactive',
            },
            // Banned IS the Black List — picking it sets isBlocked. Hidden
            // on a plan that does not include the feature, otherwise a
            // Basic company could blacklist customers from a menu while
            // the Blacklist tab that shows them stays hidden.
            ...(canUseBlacklist(this.props.user)
              ? [
                  {
                    value: STATUS_BANNED,
                    dotClass: 'customer-status-dot-banned',
                  },
                ]
              : []),
          ].map(({ value, dotClass }) => {
            const isSelected = value === status;
            return (
              <MenuItem
                key={value}
                selected={isSelected}
                onClick={() => this.handleStatusChange(value)}
                className="customer-status-menu-item"
                disableRipple
              >
                <span className={`customer-status-menu-dot ${dotClass}`} />
                <span className="customer-status-menu-label">{value}</span>
                {isSelected && (
                  <span className="customer-status-menu-check">✓</span>
                )}
              </MenuItem>
            );
          })}
        </Menu>
      </div>
    );
  };

  /**
   * Tabs this account can actually open.
   *
   *  - Match Rules IS "Match Privilege", a Regular-tier feature, so a Basic
   *    company must not see it.
   *  - Wallet needs ROLE_CUSTOMER_WALLET_PASS. It used to render for
   *    everyone and then say "your account does not have access to wallet
   *    passes" — advertising a capability the plan never included and
   *    making the customer click to find out. Hidden outright instead.
   */
  visibleTabs = () =>
    TABS.filter((t) => {
      if (t.id === TAB_RULES) return canUseMatchRules(this.props.user);
      if (t.id === TAB_WALLET) return this.hasWalletRole();
      return true;
    });

  /**
   * The tab actually on screen. A tab can disappear (plan change, role
   * change) while it is the one selected, so this falls back to Info.
   *
   * The tab BAR and the tab BODY must both go through this. Resolving it
   * only in the bar left the body still switching on the raw state, so a
   * hidden tab rendered its content underneath an Info-highlighted bar —
   * which is worse than the leak it was meant to close.
   */
  effectiveTab = () => {
    const tabs = this.visibleTabs();
    return tabs.some((t) => t.id === this.state.activeTab)
      ? this.state.activeTab
      : TAB_INFO;
  };

  renderTabBar = () => {
    const tabs = this.visibleTabs();
    const activeTab = this.effectiveTab();
    return (
      <div className="customer-tab-bar">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`customer-tab-btn ${
              activeTab === t.id ? 'customer-tab-btn-active' : ''
            }`}
            onClick={() => this.setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
    );
  };

  // `wide` opts a row out of the two-column grid used on wide screens —
  // for free-text like Comments, which reads badly in a half-width column.
  renderRow = (label, value, last, wide) => (
    <div
      className={`customer-row ${last ? 'customer-row-last' : ''} ${
        wide ? 'customer-field-wide' : ''
      }`}
    >
      <div className="customer-row-label">{label}</div>
      <div className="customer-row-value">{value || '—'}</div>
    </div>
  );

  renderInfoTab = () => {
    const c = this.props.customer || {};
    const { infoEditing } = this.state;

    if (!infoEditing) {
      return (
        <div className="customer-card">
          {this.renderRow('First Name', c.firstName)}
          {this.renderRow('Last Name', c.lastName)}
          {this.renderRow(
            'Phone',
            <span className="customer-phone-cell">
              {formatPhone(c.phone)}
              {c.phoneVerified && (
                <CheckCircleIcon
                  titleAccess="Phone verified"
                  className="customer-verified-icon"
                />
              )}
            </span>
          )}
          {this.renderRow('Date of Birth', formatDisplayDate(c.dob))}
          {this.renderRow('Comments', c.comments, true, true)}

          <Button
            variant="outlined"
            className="customer-outlined-btn"
            onClick={this.beginInfoEdit}
            fullWidth
          >
            Edit Info
          </Button>
        </div>
      );
    }

    const currentPhotoUrl =
      this.state.capturedImageURL ||
      c.capturedImageURL ||
      (c.files && c.files[0] && c.files[0].localURL) ||
      null;

    return (
      <div className="customer-card">
        <div className="customer-photo-section">
          {this.state.showVideo ? (
            <video
              ref={this.videoRef}
              className="customer-photo-video"
              autoPlay
              playsInline
              muted
            />
          ) : (
            <div
              className="customer-photo-circle"
              onClick={currentPhotoUrl ? this.openPhotoZoom : undefined}
              style={currentPhotoUrl ? { cursor: 'zoom-in' } : undefined}
            >
              {currentPhotoUrl ? (
                <img
                  src={currentPhotoUrl}
                  alt="Customer"
                  className="customer-photo-img"
                />
              ) : (
                <span className="customer-photo-placeholder">No Photo</span>
              )}
            </div>
          )}
          <canvas ref={this.canvasRef} style={{ display: 'none' }} />
          <div className="customer-photo-actions">
            {this.state.showVideo ? (
              <>
                <Button
                  className="customer-pill-btn"
                  onClick={this.captureImage}
                  disabled={!this.state.cameraReady}
                >
                  {this.state.cameraStarting ? 'Starting...' : 'Capture'}
                </Button>
                <Button
                  className="customer-outlined-btn"
                  onClick={this.switchCamera}
                  startIcon={<FlipCameraIosIcon />}
                  style={{ marginLeft: 8 }}
                >
                  {this.state.cameraFacingMode === 'environment'
                    ? 'Front'
                    : 'Back'}
                </Button>
                <Button
                  className="customer-outlined-btn"
                  onClick={this.stopCamera}
                  style={{ marginLeft: 8 }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button className="customer-pill-btn" onClick={this.openCamera}>
                {currentPhotoUrl ? 'Retake Photo' : 'Open Camera'}
              </Button>
            )}
          </div>

          {!!this.state.cameraError && (
            <div className="customer-camera-error" role="alert">
              {this.state.cameraError}
            </div>
          )}

          {!!this.state.rejectedFrameURL && (
            <div className="customer-camera-warning" role="alert">
              <div className="customer-camera-warning-text">
                {this.state.rejectedFrameReason}
              </div>
              <img
                src={this.state.rejectedFrameURL}
                alt="Rejected capture"
                className="customer-camera-warning-thumb"
              />
              <div className="customer-camera-warning-actions">
                <Button
                  className="customer-pill-btn"
                  onClick={this.handleRetakePhoto}
                >
                  Retake Photo
                </Button>
                <Button
                  size="small"
                  className="customer-camera-override-btn"
                  onClick={this.handleUseRejectedFrame}
                >
                  Use anyway
                </Button>
              </div>
            </div>
          )}

          {this.state.showVideo && this.state.videoInputs.length > 1 && (
            <div className="customer-camera-picker">
              <label htmlFor="profile-camera-select">Camera</label>
              <select
                id="profile-camera-select"
                value={this.state.selectedCameraId}
                onChange={this.handleSelectCamera}
              >
                <option value="">Default camera</option>
                {this.state.videoInputs.map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId}>
                    {d.label || `Camera ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <TextField
          className="customer-profile-input"
          variant="outlined"
          label="First Name"
          InputLabelProps={{ shrink: true }}
          value={this.state.firstName}
          onChange={(e) => this.setState({ firstName: e.target.value })}
          fullWidth
        />
        <TextField
          className="customer-profile-input"
          variant="outlined"
          label="Last Name"
          InputLabelProps={{ shrink: true }}
          value={this.state.lastName}
          onChange={(e) => this.setState({ lastName: e.target.value })}
          fullWidth
        />
        <TextField
          className={`customer-profile-input${
            this.phoneReverified() ? ' cust-phone-verified' : ''
          }`}
          variant="outlined"
          label="Phone"
          InputLabelProps={{ shrink: true }}
          value={this.state.phone}
          onChange={this.handlePhoneInput}
          fullWidth
          InputProps={{
            endAdornment: this.phoneReverified() ? (
              <InputAdornment position="end">
                <span className="cust-verified-badge">
                  <CheckCircleIcon style={{ fontSize: 16 }} /> Verified
                </span>
              </InputAdornment>
            ) : null,
          }}
        />
        {this.renderPhoneReverify()}
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            inputVariant="outlined"
            label="Date Of Birth"
            className="customer-profile-input"
            format="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
            value={this.state.dob}
            InputAdornmentProps={{ position: 'start' }}
            onChange={(dateInput) => {
              const date = new Date(dateInput?.toString());
              if (date.toString() === 'Invalid Date') return;
              this.setState({ dob: date });
            }}
            fullWidth
          />
        </MuiPickersUtilsProvider>
        <TextField
          className="customer-profile-input customer-field-wide"
          variant="outlined"
          label="Comments"
          InputLabelProps={{ shrink: true }}
          value={this.state.comments}
          onChange={(e) => this.setState({ comments: e.target.value })}
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
        />
        <div className="customer-btn-row">
          <Button
            variant="outlined"
            className="customer-outlined-btn"
            onClick={this.cancelInfoEdit}
          >
            Cancel
          </Button>
          <Button
            className="customer-primary-btn"
            onClick={this.saveInfo}
            disabled={this.needsPhoneReverify() && !this.phoneReverified()}
          >
            {this.needsPhoneReverify() && !this.phoneReverified()
              ? 'Verify Phone to Save'
              : 'Save'}
          </Button>
        </div>
      </div>
    );
  };

  renderPointsTab = () => {
    const c = this.props.customer || {};
    const { pointsEditing, points } = this.state;
    const canEdit = this.canEditPoints();

    return (
      <React.Fragment>
        <div className="customer-points-card">
          <div className="customer-points-label">CURRENT POINTS</div>
          {pointsEditing && canEdit ? (
            <div className="customer-points-editor">
              <Button
                className="customer-points-adjust"
                onClick={() => this.adjustPoints(-5)}
              >
                −
              </Button>
              <input
                type="number"
                className="customer-points-input"
                value={points}
                onFocus={() => {
                  if (this.state.points === 0) {
                    this.setState({ points: '' });
                  }
                }}
                onBlur={() => {
                  if (this.state.points === '' || this.state.points === null) {
                    this.setState({ points: 0 });
                  }
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    this.setState({ points: '' });
                  } else {
                    const n = parseInt(v, 10);
                    this.setState({ points: isNaN(n) ? '' : n });
                  }
                }}
              />
              <Button
                className="customer-points-adjust"
                onClick={() => this.adjustPoints(5)}
              >
                +
              </Button>
            </div>
          ) : (
            <div
              className="customer-points-value"
              onClick={canEdit ? this.beginPointsEdit : undefined}
              role={canEdit ? 'button' : undefined}
              tabIndex={canEdit ? 0 : undefined}
              style={canEdit ? undefined : { cursor: 'default' }}
            >
              {points}
            </div>
          )}
        </div>

        <div className="customer-card">
          <div className="customer-row">
            <div className="customer-row-label">Registered By</div>
            <div className="customer-row-value">
              {(c.employeeName || '')
                .split(/\s+/)
                .filter((p) => p && p !== 'null' && p !== 'undefined')
                .join(' ') || '—'}
            </div>
          </div>
          <div className="customer-row customer-row-last">
            <div className="customer-row-label">Registered Date</div>
            <div className="customer-row-value">
              {(() => {
                const regDate = c.registrationDate;
                if (!regDate) return '—';
                const isoMatch = String(regDate).match(
                  /^(\d{4})-(\d{2})-(\d{2})(.*)/
                );
                if (isoMatch) {
                  const [, year, month, day, rest] = isoMatch;
                  return `${month}/${day}/${year}${rest}`;
                }
                return regDate;
              })()}
            </div>
          </div>
        </div>

        {pointsEditing && canEdit && (
          <div className="customer-btn-row">
            <Button
              variant="outlined"
              className="customer-outlined-btn"
              onClick={this.cancelPointsEdit}
            >
              Cancel
            </Button>
            <Button className="customer-primary-btn" onClick={this.savePoints}>
              Save
            </Button>
          </div>
        )}

        {this.renderRecentVisits()}
        {this.canViewPointAudit() && this.renderPointAudit()}
      </React.Fragment>
    );
  };

  // Last visit string from the server is "MM/dd/yyyy hh:mm a"; split into the
  // calendar date (Mon DD, YYYY) and the time of day for a two-line row.
  parseVisit = (visitStr) => {
    if (!visitStr) return { dateLabel: '—', timeLabel: '' };
    const d = new Date(visitStr);
    if (isNaN(d.getTime())) {
      const parts = String(visitStr).split(' ');
      return {
        dateLabel: parts[0] || visitStr,
        timeLabel: parts.slice(1).join(' '),
      };
    }
    const dateLabel = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeLabel = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return { dateLabel, timeLabel };
  };

  renderRecentVisits = () => {
    const { recentVisits, recentVisitsLoading, recentVisitsLoaded } =
      this.state;

    return (
      <div className="customer-card">
        <div className="customer-visits-header">
          <div className="customer-card-title">Recent Visits</div>
          <div className="customer-visits-badge">Last 20</div>
        </div>

        {recentVisitsLoading && (
          <div className="customer-visits-empty">Loading visits…</div>
        )}

        {!recentVisitsLoading &&
          recentVisitsLoaded &&
          recentVisits.length === 0 && (
            <div className="customer-visits-empty">No visits recorded yet.</div>
          )}

        {!recentVisitsLoading && recentVisits.length > 0 && (
          <ul className="customer-visits-list">
            {recentVisits.map((v, i) => {
              const { dateLabel, timeLabel } = this.parseVisit(v.lastVisit);
              const isFirst = i === 0;
              return (
                <li
                  key={`visit-${i}-${v.lastVisit}`}
                  className="customer-visit-row"
                >
                  <div className="customer-visit-marker">
                    <span
                      className={`customer-visit-dot ${
                        isFirst ? 'customer-visit-dot-active' : ''
                      }`}
                    />
                    {i < recentVisits.length - 1 && (
                      <span className="customer-visit-line" />
                    )}
                  </div>
                  <div className="customer-visit-body">
                    <div className="customer-visit-date">{dateLabel}</div>
                    {timeLabel && (
                      <div className="customer-visit-time">{timeLabel}</div>
                    )}
                  </div>
                  <div className="customer-visit-points">
                    <span className="customer-visit-points-value">
                      {v.points}
                    </span>
                    <span className="customer-visit-points-label">pts</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  // "Aug 24, 2026 · 3:04 PM" for a point-audit row timestamp.
  formatAuditStamp = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const dateLabel = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeLabel = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${dateLabel} · ${timeLabel}`;
  };

  renderPointAudit = () => {
    const {
      pointAudits,
      pointAuditTotal,
      pointAuditHasMore,
      pointAuditLoaded,
      pointAuditLoading,
      pointAuditLoadingMore,
    } = this.state;

    return (
      <div className="customer-card">
        <div className="customer-visits-header">
          <div className="customer-card-title">Point Change History</div>
          {pointAuditTotal > 0 && (
            <div className="customer-visits-badge">
              {pointAudits.length} of {pointAuditTotal}
            </div>
          )}
        </div>

        {pointAuditLoading && (
          <div className="customer-visits-empty">Loading history…</div>
        )}

        {!pointAuditLoading && pointAuditLoaded && pointAudits.length === 0 && (
          <div className="customer-visits-empty">
            No point changes recorded yet.
          </div>
        )}

        {!pointAuditLoading && pointAudits.length > 0 && (
          <React.Fragment>
            <ul className="customer-audit-list">
              {pointAudits.map((row, i) => {
                const delta = Number(row.delta) || 0;
                const up = delta > 0;
                return (
                  <li
                    key={row.auditId || `point-audit-${i}`}
                    className="customer-audit-row"
                  >
                    <div
                      className={`customer-audit-delta ${
                        up ? 'customer-audit-up' : 'customer-audit-down'
                      }`}
                    >
                      {up ? '+' : '−'}
                      {Math.abs(delta)}
                    </div>
                    <div className="customer-audit-body">
                      <div className="customer-audit-change">
                        {row.oldPoints} → <strong>{row.newPoints}</strong> pts
                      </div>
                      <div className="customer-audit-meta">
                        {row.userName || 'system'} ·{' '}
                        {this.formatAuditStamp(row.changedAt)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {pointAuditHasMore && (
              <div className="customer-audit-more">
                <Button
                  variant="outlined"
                  className="customer-outlined-btn"
                  disabled={pointAuditLoadingMore}
                  onClick={() => this.fetchPointAudits(pointAudits.length)}
                >
                  {pointAuditLoadingMore ? 'Loading…' : 'Show more'}
                </Button>
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    );
  };

  renderRulesTab = () => {
    const {
      isRule,
      timeLimit,
      maxMatchesPerDay,
      maxAmountPerDay,
      matchValues,
      rulesDirty,
    } = this.state;

    const adminOk = this.isAdmin();

    const capNum = Number(maxAmountPerDay);
    const total = matchValues.reduce((acc, v) => acc + (Number(v) || 0), 0);
    const hasCap = !Number.isNaN(capNum) && capNum > 0;
    const overCap = hasCap && total > capNum;

    return (
      <React.Fragment>
        <div className="customer-card">
          <div className="customer-toggle-row">
            <div className="customer-toggle-info">
              <div className="customer-toggle-title">Privileged Match Rule</div>
              <div className="customer-toggle-desc">
                When enabled, this customer is subject to custom match limits
                rather than global rules.
              </div>
            </div>
            <button
              type="button"
              className={`customer-toggle ${
                isRule ? 'customer-toggle-on' : 'customer-toggle-off'
              }`}
              onClick={this.toggleRule}
              disabled={!adminOk}
            >
              <span className="customer-toggle-knob" />
            </button>
          </div>

          {isRule && (
            <div className="customer-rule-fields">
              <TextField
                className="customer-profile-input"
                variant="outlined"
                label="Wait Time (hours)"
                name="timeLimit"
                type="number"
                value={timeLimit}
                onChange={this.handleRuleField}
                InputLabelProps={{ shrink: true }}
                disabled={!adminOk}
                fullWidth
              />
              <TextField
                className="customer-profile-input"
                variant="outlined"
                label="Daily Match Limit"
                name="maxMatchesPerDay"
                type="number"
                value={maxMatchesPerDay}
                onChange={this.handleRuleField}
                InputLabelProps={{ shrink: true }}
                disabled={!adminOk}
                fullWidth
              />
              <TextField
                className="customer-profile-input"
                variant="outlined"
                label="Daily Match Cap (7am–7am)"
                name="maxAmountPerDay"
                type="number"
                value={maxAmountPerDay}
                onChange={this.handleRuleField}
                InputLabelProps={{ shrink: true }}
                disabled={!adminOk}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                helperText="max total per day"
              />
            </div>
          )}
        </div>

        {isRule && (
          <div className="customer-card">
            <div className="customer-card-title">Match Amounts</div>

            {matchValues.map((val, i) => (
              <div className="customer-match-row" key={`match-${i}`}>
                <div className="customer-match-index">{i + 1}</div>
                <TextField
                  className="customer-profile-input customer-match-input"
                  variant="outlined"
                  type="number"
                  value={val}
                  onChange={(e) =>
                    this.handleMatchValueChange(i, e.target.value)
                  }
                  disabled={!adminOk}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
                <button
                  type="button"
                  className="customer-match-delete"
                  onClick={() => this.removeMatchValue(i)}
                  disabled={!adminOk}
                  aria-label="Delete match amount"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}

            {adminOk && (
              <button
                type="button"
                className="customer-add-match-btn"
                onClick={this.addMatchValue}
              >
                + Add Match Amount
              </button>
            )}

            <div
              className={`customer-cap-indicator ${
                overCap
                  ? 'customer-cap-indicator-bad'
                  : 'customer-cap-indicator-good'
              }`}
            >
              {overCap ? (
                <WarningIcon className="customer-cap-icon" fontSize="small" />
              ) : (
                <CheckCircleIcon
                  className="customer-cap-icon"
                  fontSize="small"
                />
              )}
              <span>
                {overCap
                  ? `Exceeds cap by $${(total - capNum).toFixed(
                      2
                    )} (total $${total.toFixed(2)})`
                  : hasCap
                  ? `Total $${total.toFixed(2)} of $${capNum.toFixed(2)} cap`
                  : `Total $${total.toFixed(2)}`}
              </span>
            </div>
          </div>
        )}

        {adminOk && rulesDirty && (
          <div className="customer-btn-row">
            <Button className="customer-primary-btn" onClick={this.saveRules}>
              Save Match Rules
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  };

  renderWalletTab = () => {
    if (!this.hasWalletRole()) {
      return (
        <div className="customer-card">
          <div className="customer-info-banner customer-info-banner-yellow">
            <InfoOutlinedIcon className="customer-info-banner-icon" />
            <span>Your account does not have access to wallet passes.</span>
          </div>
        </div>
      );
    }

    const { walletType, walletQrUrl, isGeneratingWallet, walletChecked } =
      this.state;

    return (
      <div className="customer-card">
        <div className="customer-wallet-type-group">
          <div className="customer-row-label">Wallet Type</div>
          <div className="customer-wallet-type-choices">
            {[
              { id: 'apple', label: 'Apple Wallet' },
              { id: 'google', label: 'Google Wallet' },
            ].map((w) => (
              <button
                key={w.id}
                type="button"
                className={`customer-wallet-choice ${
                  walletType === w.id ? 'customer-wallet-choice-active' : ''
                }`}
                onClick={() =>
                  this.setState({
                    walletType: w.id,
                    walletQrUrl: null,
                    walletPassUrl: null,
                  })
                }
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {walletChecked && !walletQrUrl && (
          <div className="customer-info-banner customer-info-banner-yellow">
            <InfoOutlinedIcon className="customer-info-banner-icon" />
            <span>No wallet pass exists yet. Generate one below.</span>
          </div>
        )}

        {walletQrUrl && (
          <Button
            variant="outlined"
            className="customer-outlined-btn"
            onClick={this.openWalletDialog}
            startIcon={<VisibilityIcon />}
            fullWidth
          >
            View Wallet Pass
          </Button>
        )}

        <Button
          className="customer-primary-btn"
          onClick={this.generateWalletPass}
          disabled={isGeneratingWallet}
          fullWidth
        >
          {isGeneratingWallet ? 'Generating...' : 'Generate Wallet Pass'}
        </Button>

        <Dialog
          open={this.state.walletDialogOpen}
          onClose={this.closeWalletDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle style={{ textAlign: 'center' }}>
            Scan to Add Wallet Pass
          </DialogTitle>
          <DialogContent style={{ textAlign: 'center' }}>
            {walletQrUrl && (
              <img
                src={walletQrUrl}
                alt="Wallet QR"
                style={{ width: 280, height: 280, margin: '16px auto' }}
              />
            )}
            <Typography
              variant="body2"
              style={{ color: '#666', marginTop: 12 }}
            >
              This pass is device-bound and cannot be shared
            </Typography>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  renderActiveTab = () => {
    switch (this.effectiveTab()) {
      case TAB_INFO:
        return this.renderInfoTab();
      case TAB_POINTS:
        return this.renderPointsTab();
      case TAB_RULES:
        return this.renderRulesTab();
      case TAB_WALLET:
        return this.renderWalletTab();
      default:
        return null;
    }
  };

  render() {
    if (!this.props.customer) {
      return (
        <React.Fragment>
          <TopToolbar
            pageName="Customer Profile"
            menuItems={[
              {
                title: 'Back',
                icon: <KeyboardBackspaceIcon />,
                onClick: () => this.props.handleBack(),
              },
            ]}
          />
          <div className="customer-profile-wrapper">
            <div className="customer-card">Customer could not be loaded.</div>
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <TopToolbar
          pageName="Customer Profile"
          menuItems={[
            {
              title: 'Back',
              icon: <KeyboardBackspaceIcon />,
              onClick: () => this.props.handleBack(),
            },
          ]}
        />

        <div className="customer-profile-wrapper">
          {this.renderHeader()}
          {/* Tab bar stays OUTSIDE the guard so a viewer can move through
              every tab and read the whole profile. Only the tab content —
              where the save / points / rules buttons live — is inerted.
              See constants/readOnlyAccess.js. */}
          {this.renderTabBar()}
          <WriteGuard mode="disable" noTooltip>
            <div className="customer-tab-content">{this.renderActiveTab()}</div>
          </WriteGuard>
        </div>

        {this.renderPhotoZoomDialog()}
      </React.Fragment>
    );
  }

  renderPhotoZoomDialog = () => {
    const c = this.props.customer || {};
    const zoomUrl =
      this.state.capturedImageURL ||
      c.capturedImageURL ||
      (c.files && c.files[0] && c.files[0].localURL) ||
      null;

    if (!zoomUrl) return null;

    return (
      <Dialog
        open={this.state.photoZoomOpen}
        onClose={this.closePhotoZoom}
        maxWidth="md"
        fullWidth
      >
        <DialogContent
          onClick={this.closePhotoZoom}
          style={{
            padding: 0,
            backgroundColor: '#000',
            cursor: 'zoom-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={zoomUrl}
            alt="Customer"
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              display: 'block',
              margin: '0 auto',
            }}
          />
        </DialogContent>
      </Dialog>
    );
  };
}

export default CustomerProfile;
